import type { StepConfig } from "../types/step";
import { stepFieldsMap } from "./fields";

// Step flow templates for different user types
const STEP_FLOW_TEMPLATES = {
	// Full property flow for owners and agents

	DEFAULT: [
		{ key: "profile", title: "Profile", description: "Tell us about yourself and your property type" },
		{ key: "address", title: "Address Map and Project Info", description: "Help us locate your property precisely" },
		{ key: "basic", title: "Basic Property Details", description: "Essential information about your property" },
	],

	OWNER: [
		{ key: "profile", title: "Profile", description: "Tell us about yourself and your property type" },
		{ key: "address", title: "Address Map and Project Info", description: "Help us locate your property precisely" },
		{ key: "basic", title: "Basic Property Details", description: "Essential information about your property" },
		{ key: "additional", title: "Additional Property Details", description: "Detailed specifications and features" },
		{
			key: "other",
			title: "Project / House / Community Details",
			description: "Information about the project or house or community and amenities",
		},
		{ key: "uploads", title: "Uploads", description: "Upload images and required documents" },
		{ key: "fee", title: "Fee Confirmation", description: "Review and confirm service terms" },
		{ key: "slot", title: "Slot Confirmation", description: "Schedule your property visit" },
		{ key: "success", title: "Success", description: "Your property has been successfully listed" },
	],

	AGENT: [
		{ key: "profile", title: "Profile", description: "Tell us about yourself and your property type" },
		{ key: "address", title: "Address Map and Project Info", description: "Help us locate your property precisely" },
		{ key: "basic", title: "Basic Property Details", description: "Essential information about your property" },
		{ key: "success", title: "Success", description: "Your property has been successfully listed" },
	],

	// Simplified project flow for builders, landowners, and investors
	BUILDER_LO_INVESTOR: [
		{ key: "profile", title: "Profile", description: "Tell us about yourself and your project type" },
		{
			key: "builderOrLandownerOrInvestorProject",
			title: "Project Details",
			description: "Information about your project",
		},
		{ key: "success", title: "Success", description: "Your project has been successfully registered" },
	],
} as const;

// Relationship to flow mapping
const RELATIONSHIP_FLOWS: Record<string, keyof typeof STEP_FLOW_TEMPLATES> = {
	"": "DEFAULT",
	owner: "OWNER",
	agent: "AGENT",
	investor: "BUILDER_LO_INVESTOR",
	landOwner: "BUILDER_LO_INVESTOR",
	builder: "BUILDER_LO_INVESTOR",
};

// Custom descriptions for project-based relationships
const PROJECT_DESCRIPTIONS: Record<string, string> = {
	investor: "Tell us about your investment project",
	landOwner: "Tell us about your number of project inventory",
	builder: "Tell us about your number of project inventory",
};

/**
 * Get step configurations for a relationship - optimized for efficiency
 */
export const getSteps = ({ relationship = "" }: { relationship?: string } = {}): StepConfig[] => {
	const flowType = RELATIONSHIP_FLOWS[relationship] || "DEFAULT";
	const template = STEP_FLOW_TEMPLATES[flowType];

	return template.map((step, index) => ({
		id: index + 1,
		title: step.title,
		key: step.key,
		description: getStepDescription({ stepKey: step.key, relationship, defaultDescription: step.description }),
		fields: stepFieldsMap[step.key] || [],
	}));
};

/**
 * Get step description with relationship-specific customization
 */
export function getStepDescription({
	stepKey,
	relationship,
	defaultDescription,
}: {
	stepKey: string;
	relationship?: string;
	defaultDescription: string;
}): string {
	// Customize project details description based on relationship
	if (stepKey === "builderOrLandownerOrInvestorProject" && relationship && PROJECT_DESCRIPTIONS[relationship]) {
		return PROJECT_DESCRIPTIONS[relationship];
	}
	return defaultDescription;
}

/**
 * Get next step process for URL navigation
 */
export const getNextStepProcess = ({
	relationship = "",
	currentStepKey,
}: {
	relationship?: string;
	currentStepKey: string;
}): string => {
	const flowType = RELATIONSHIP_FLOWS[relationship] || "DEFAULT";
	const stepsConfig = STEP_FLOW_TEMPLATES[flowType];
	const currentStepIndex = stepsConfig.findIndex(step => step.key === currentStepKey);
	const nextStep = stepsConfig[currentStepIndex + 1];
	return nextStep?.key || "profile";
};
